/** P5：生成和保存分成两步，保存按钮保留真实点击，手机浏览器也能下载。 */
import type { SnapshotResponse } from '@star-sea/shared';
import { buildAlbum, prepareAlbum, type AlbumPart, type AlbumPlan } from './album.ts';
import { el } from './dom.ts';
import { posterPdf, posterPng, renderPoster } from './poster.ts';

interface ExportOptions {
  viewerId: string;
  offline: boolean;
  loadSnapshot(): Promise<SnapshotResponse>;
  loadPhoto(id: string, signal?: AbortSignal): Promise<Blob>;
  onClose(): void;
}

const sizeOf = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;
const filename = (s: string) => s.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').slice(0, 64) || '星海';

export function exportDialog(o: ExportOptions): { close(): void } {
  const dialog = el('dialog', { class: 'dialog export-dialog', attrs: { 'aria-labelledby': 'export-title' } });
  const title = el('h2', { class: 'dialog-title', text: '把星海带走', attrs: { id: 'export-title' } });
  const subtitle = el('p', { class: 'field-hint', text: o.offline ? '正在离线浏览，将导出上次同步的内容；画册需要所选照片已缓存在这台设备。' : '留一张星图，或把这些日子装成一本可以离线翻阅的画册。' });
  const year = el('select', { class: 'export-select', attrs: { 'aria-label': '导出年份' } }, el('option', { text: '全部年份', attrs: { value: '' } }));
  const summary = el('p', { class: 'export-summary' });
  const status = el('p', { class: 'export-status', text: '正在读取星海…', attrs: { role: 'status', 'aria-live': 'polite' } });
  const error = el('p', { class: 'form-error', attrs: { role: 'alert' } });
  const preview = el('div', { class: 'export-preview' });
  const posterPane = el('section', { class: 'export-poster' },
    preview,
    el('p', { class: 'field-hint', text: 'A4 竖版 · 2480 × 3508 像素 · 300 DPI。海报只画星点与称呼，不含照片和故事。' }),
  );
  const albumPane = el('section', { class: 'export-album' },
    el('div', { class: 'export-book', attrs: { 'aria-hidden': 'true' } }, el('span', { text: '星海' }), el('small', { text: '把我们的日子，留在这里' })),
    el('p', { text: '一本无需登录、无需网络的 HTML 画册。照片、故事、已揭晓的双面文字和已拆开的胶囊都装在里面。' }),
    el('p', { class: 'field-hint', text: '使用 512 档缩略图，不是原图备份；只含照片和文字，不包含录音。超过约 150 MB 按年份拆分，单年太大再分卷；每次保存一卷。文件包含私人回忆，请只交给想分享的人。' }),
  );
  albumPane.hidden = true;
  const posterTab = el('button', { class: 'btn', text: '星图海报', attrs: { type: 'button', 'aria-pressed': 'true' } });
  const albumTab = el('button', { class: 'btn', text: '离线画册', attrs: { type: 'button', 'aria-pressed': 'false' } });
  const png = el('button', { class: 'btn btn-primary', text: '生成 PNG', attrs: { type: 'button' } });
  const pdf = el('button', { class: 'btn', text: '生成 PDF', attrs: { type: 'button' } });
  const album = el('button', { class: 'btn btn-primary', text: '生成画册', attrs: { type: 'button' } });
  album.hidden = true;
  const cancel = el('button', { class: 'btn', text: '取消生成', attrs: { type: 'button' } });
  cancel.hidden = true;
  const next = el('button', { class: 'btn', text: '已保存，继续下一卷', attrs: { type: 'button' } });
  next.hidden = true;
  const download = el('a', { class: 'btn btn-primary', text: '保存文件' });
  download.hidden = true;
  const closeButton = el('button', { class: 'btn btn-ghost', text: '关闭', attrs: { type: 'button' } });
  const actions = el('div', { class: 'dialog-actions export-actions' }, png, pdf, album, cancel, download, next, closeButton);
  dialog.append(title, subtitle, el('div', { class: 'export-tabs' }, posterTab, albumTab),
    el('label', { class: 'export-range' }, el('span', { text: '选择日子' }), year), summary,
    posterPane, albumPane, error, status, actions);

  let snapshot: SnapshotResponse | null = null;
  let plan: AlbumPlan | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let mode: 'poster' | 'album' = 'poster';
  let busy = false, closed = false;
  let revision = 0;
  let controller: AbortController | null = null;
  let iterator: AsyncGenerator<AlbumPart> | null = null;
  let downloadUrl: string | null = null;
  let delivered = 0;

  const clearFile = () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    downloadUrl = null;
    download.removeAttribute('href');
    download.hidden = true;
    next.hidden = true;
  };
  const releaseCanvas = () => {
    // Safari 对画布总内存也有限制，切换年份和关窗时立即归还像素缓冲。
    if (canvas) { canvas.width = 0; canvas.height = 0; }
    canvas = null;
    preview.replaceChildren();
  };
  const controls = () => {
    year.disabled = busy || !snapshot || !!iterator;
    posterTab.disabled = busy;
    albumTab.disabled = busy;
    png.disabled = busy || !canvas;
    pdf.disabled = busy || !canvas;
    album.disabled = busy || !plan?.items.length || !!iterator;
    cancel.hidden = !busy && !iterator;
    cancel.textContent = busy ? '取消生成' : '结束分卷';
    dialog.setAttribute('aria-busy', String(busy));
  };
  const stop = () => {
    revision++;
    controller?.abort();
    controller = null;
    const previous = iterator;
    iterator = null;
    // return 在当前 next 结束后释放它持有的这一卷；取消错误不再写回已关闭的对话框。
    if (previous) void previous.return(undefined).catch(() => undefined);
    busy = false;
    controls();
  };
  const close = () => {
    if (closed) return;
    closed = true;
    stop();
    clearFile();
    releaseCanvas();
    dialog.close();
    dialog.remove();
    o.onClose();
  };
  const showFile = (blob: Blob, name: string) => {
    clearFile();
    downloadUrl = URL.createObjectURL(blob);
    download.href = downloadUrl;
    download.download = name;
    download.textContent = `保存 ${name.endsWith('.html') ? '本卷' : name.endsWith('.pdf') ? 'PDF' : 'PNG'} · ${sizeOf(blob.size)}`;
    download.hidden = false;
  };
  const redraw = () => {
    if (!snapshot) return;
    stop();
    clearFile();
    releaseCanvas();
    error.textContent = '';
    status.textContent = '';
    delivered = 0;
    try {
      plan = prepareAlbum(snapshot, o.viewerId, year.value ? Number(year.value) : undefined);
      summary.textContent = `${year.value ? `${year.value} 年` : '全部年份'} · ${plan.photoCount} 张照片 · ${plan.items.length} 页回忆`;
      if (mode === 'poster') {
        // 两种导出共用同一份可见内容名单，未拆胶囊不会变成海报上的星。
        const ids = new Set(plan.items.map((item) => item.photoId).filter(Boolean));
        const photos = [...snapshot.photos, ...(snapshot.capsules ?? []).flatMap((c) => c.photo ? [c.photo] : [])].filter((p) => ids.has(p.id));
        canvas = renderPoster(snapshot, { photos, rangeLabel: year.value ? `${year.value} 年` : '全部年份' });
        canvas.setAttribute('role', 'img');
        canvas.setAttribute('aria-label', `${snapshot.space.name}的 A4 星图海报预览`);
        preview.append(canvas);
      } else if (!plan.items.length) status.textContent = '这些日子还没有可以装进画册的回忆。';
    } catch (e) {
      error.textContent = e instanceof Error ? e.message : String(e);
    }
    controls();
  };
  const switchMode = (to: typeof mode) => {
    if (busy || mode === to) return;
    mode = to;
    posterTab.setAttribute('aria-pressed', String(to === 'poster'));
    albumTab.setAttribute('aria-pressed', String(to === 'album'));
    posterPane.hidden = png.hidden = pdf.hidden = to !== 'poster';
    albumPane.hidden = album.hidden = to !== 'album';
    redraw();
  };
  const makePoster = async (format: 'png' | 'pdf') => {
    if (busy || !canvas || !snapshot) return;
    const current = ++revision;
    busy = true;
    clearFile();
    error.textContent = '';
    status.textContent = `正在生成 ${format.toUpperCase()}…`;
    controls();
    try {
      const blob = await (format === 'png' ? posterPng(canvas) : posterPdf(canvas));
      if (closed || current !== revision) return;
      showFile(blob, `${filename(snapshot.space.name)}-${year.value || '全部年份'}-A4.${format}`);
      status.textContent = '已生成，点保存将文件留到这台设备。打印 PDF 时选择 A4、实际大小。';
    } catch (e) {
      if (!closed && current === revision) error.textContent = e instanceof Error ? e.message : String(e);
    } finally {
      if (!closed && current === revision) { busy = false; controls(); }
    }
  };
  const advanceAlbum = async () => {
    if (busy || !iterator) return;
    const current = ++revision;
    busy = true;
    clearFile();
    error.textContent = '';
    status.textContent = '正在装订这一卷…';
    controls();
    try {
      const result = await iterator.next();
      if (closed || current !== revision) return;
      if (result.done) {
        iterator = null;
        status.textContent = '所有分卷已生成。';
      } else {
        const part = result.value;
        delivered += part.count;
        showFile(part.blob, part.name);
        // 必须由用户点过保存，才放出下一卷；不会自动触发一串下载。
        next.disabled = true;
        next.hidden = delivered >= (plan?.items.length ?? 0);
        if (next.hidden) { void iterator.return(undefined); iterator = null; }
        status.textContent = `第 ${part.index} 卷已生成 · ${part.count} 页 · ${part.photos} 张照片。${next.hidden ? '这是最后一卷。' : '保存本卷后继续；生成下一卷会释放本卷。'}`;
      }
    } catch (e) {
      if (closed || current !== revision) return;
      controller?.abort();
      iterator = null;
      error.textContent = `画册没有生成完整：${e instanceof Error ? e.message : String(e)}。可重试，或选择较小的年份范围。`;
      status.textContent = delivered ? `此前已生成 ${delivered} 页，已保存的文件仍可打开。` : '';
    } finally {
      if (!closed && current === revision) { busy = false; controls(); }
    }
  };
  album.addEventListener('click', () => {
    if (busy || !plan?.items.length || iterator) return;
    delivered = 0;
    controller = new AbortController();
    iterator = buildAlbum(plan, o.loadPhoto, {
      signal: controller.signal,
      onProgress: (p) => { if (!closed && busy) status.textContent = `正在收集照片 ${p.completed} / ${p.total} · 已读取 ${sizeOf(p.bytes)}`; },
    });
    void advanceAlbum();
  });
  download.addEventListener('click', () => { next.disabled = false; });
  next.addEventListener('click', () => void advanceAlbum());
  cancel.addEventListener('click', () => { stop(); clearFile(); status.textContent = '已取消。已经保存的文件仍可打开。'; });
  png.addEventListener('click', () => void makePoster('png'));
  pdf.addEventListener('click', () => void makePoster('pdf'));
  posterTab.addEventListener('click', () => switchMode('poster'));
  albumTab.addEventListener('click', () => switchMode('album'));
  year.addEventListener('change', redraw);
  closeButton.addEventListener('click', close);
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  document.body.append(dialog);
  dialog.showModal();
  controls();
  void o.loadSnapshot().then((s) => {
    if (closed) return;
    snapshot = s;
    const all = prepareAlbum(s, o.viewerId);
    for (const y of all.years) year.append(el('option', { text: `${y} 年`, attrs: { value: String(y) } }));
    redraw();
  }).catch((e: unknown) => {
    if (!closed) { status.textContent = ''; error.textContent = `无法读取星海：${e instanceof Error ? e.message : String(e)}。关闭后可重试。`; }
  });
  return { close };
}
